import { useCallback, useEffect, useRef } from 'react';

interface UseDialogPortalOptions {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Shared dialog-shell behaviour for portal-based modals: a stable portal host
 * appended to <body>, plus focus save/restore, body scroll-lock, and
 * Escape-to-close while open. Returns the element to render the dialog into via
 * `ReactDOM.createPortal`.
 *
 * Both `common/Modal.tsx` and the Admin Reports `DefineReportModal` consume this
 * so the infra lives in one place — a fix here reaches every dialog.
 */
export function useDialogPortal({
  isOpen,
  onClose,
}: UseDialogPortalOptions): HTMLDivElement {
  const elRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  if (!elRef.current) elRef.current = document.createElement('div');

  useEffect(() => {
    const el = elRef.current!;
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus();
    };
  }, [isOpen, onClose]);

  return elRef.current;
}

/**
 * A close handler that no-ops while a mutation is in flight, so Escape / backdrop
 * clicks can't dismiss a dialog mid-save (matching a disabled Cancel button).
 */
export function useGuardedClose(
  onClose: () => void,
  blocked: boolean
): () => void {
  return useCallback(() => {
    if (!blocked) onClose();
  }, [onClose, blocked]);
}
